import React from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers";
import {
  PhoneIcon,
  MapPinIcon,
  IdentificationIcon,
} from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { Avatar, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddManager } from "./add-manager";
import { toJS } from "mobx";
import ThumbnailImage from "../../components-new/Thumbnail";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { safe_get } from "../../report-visuals";
import { useParams } from "react-router-dom";

export const Profile = observer(() => {
  const { userStore, voterListStore } = useStore();
  const [t] = useTranslation("user_management");

  const {
    activeUser,
    userPermissionsAndParts,
    delete_manager,
    active_manager,
  } = userStore;

  const user = toJS(activeUser);
  const userDetails = toJS(userPermissionsAndParts);
  const manager = userDetails?.manager;

  const { projectid } = useParams<{ projectid: string }>();
  const numericProjectId = parseInt(projectid || "0", 10);
  const numericUserId = parseInt(activeUser?.id || 0, 10);

  const handleManagerAdd = () => userStore.setAddManager(true);

  const { mutate } = useMutation({
    mutationFn: async (payload: any) => {
      return await userStore.remove_manager_reportees(payload);
    },

    onSuccess: async () => {
      await userStore.get_user_permissions_and_parts({
        userid: numericUserId,
        projectid: numericProjectId,
      });

      toast.success("Manager deleted successfully!");
      userStore.setactiveManager(null);
      userStore.set_delete_manager(false);
    },

    onError: () => {
      toast.error("Something went wrong while deleting");
    },
  });

  const handleEditManager = (manager) => {
    userStore.setSelectedManager(manager);
    userStore.setAddManager(true);
  };

  const handleDeleteManager = (manager) => {
    userStore.setactiveManager(manager);
    userStore.set_delete_manager(true);
  };

  const onCancel = () => {
    userStore.set_delete_manager(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 w-full mb-2">
      <div className="flex items-start gap-6">
        <Avatar sx={{ width: 150, height: 150 }} alt={user?.name}>
          {user?.profileimage && (
            <ThumbnailImage
              key={user?.id}
              s3Key={user?.profileimage}
              bucket="urn-assets"
              width={150}
              height={150}
            />
          )}
        </Avatar>

        <div className="flex-1">
          <h2 className="text-2xl font-semibold text-gray-800">
            {user?.name}
            <span className="text-indigo-600 ml-2 capitalize text-base">
              ({user?.role})
            </span>
          </h2>

          <div className="mt-3 grid grid-cols-2 gap-y-2 text-gray-600">
            <div className="flex items-center gap-2">
              <IdentificationIcon className="h-5 w-5 text-gray-400" />
              <span>{user?.id}</span>
            </div>

            <div className="flex items-center gap-2">
              <PhoneIcon className="h-5 w-5 text-gray-400" />
              <span>{user?.phonenum}</span>
            </div>

            <div className="flex items-center gap-2 col-span-2 gap-x-4">
              <MapPinIcon className="h-5 w-5 text-gray-400" />
              <span>
                {user?.address} {user?.city}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pl-4">
          <span
            className={`px-4 py-1 text-sm font-semibold rounded-full border ${
              user?.active
                ? "border-green-400 text-green-600 bg-green-50"
                : "border-red-400 text-red-600 bg-red-50"
            }`}
          >
            {user?.active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-gray-800 font-semibold">Manager</h4>
          <button
            onClick={handleManagerAdd}
            className="p-1 rounded-full hover:bg-indigo-50"
          >
            <AddCircleRoundedIcon className="text-indigo-600" />
          </button>
        </div>
        {manager && Object.keys(manager).length > 0 ? (
          <div className="flex items-center justify-between bg-gray-50 border rounded-xl p-3">
            <div className="flex items-center gap-3">
              <Avatar sx={{ width: 45, height: 45 }} alt={manager?.name}>
                {manager?.profileuri && (
                  <ThumbnailImage
                    key={manager?.id}
                    s3Key={manager?.profileuri}
                    bucket="urn-assets"
                    width={45}
                    height={45}
                  />
                )}
              </Avatar>

              <div>
                <p className="font-semibold text-gray-800">{manager?.name}</p>
                {manager?.id && (
                  <p className="text-sm text-gray-500">{manager?.id}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <IconButton
                size="small"
                onClick={() => handleEditManager(manager)}
              >
                <EditIcon fontSize="small" className="text-indigo-600" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => handleDeleteManager(manager)}
              >
                <DeleteIcon fontSize="small" className="text-red-600" />
              </IconButton>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No manager assigned</p>
        )}
      </div>

      {!userDetails?.has_voter_profile && (
        <div className="mt-6 text-sm">
          User Attach VoterProfile To{" "}
          <span
            className="text-indigo-600 underline cursor-pointer font-medium"
            onClick={() => {
              voterListStore.setShowVoterlist(true);
              voterListStore.setRenderInOverlay(true);
            }}
          >
            {t("connect")}
          </span>
        </div>
      )}

      <AddManager />

      {delete_manager && (
        <DeleteModal
          onDeleteClick={() => {
            const managerId = safe_get(active_manager, "id", null);
            const reporteeId = numericUserId;

            mutate({
              projectid: numericProjectId,
              mgrid: managerId,
              reportee: reporteeId,
            });
          }}
          onCancelClick={onCancel}
          isOpen={delete_manager}
          id={active_manager}
          desc="Do you want to delete this Manager?"
        />
      )}
    </div>
  );
});
